import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Lock, Shield, DollarSign, Plus, Pencil, Trash2, Loader2, Upload,
  Download, AlertTriangle, CheckCircle2, Clock, Users, Settings,
  Search, X, Calculator, TrendingUp, TrendingDown, RefreshCw, Mail, BarChart3, Trophy, Send, CopyX, History, Eye, FileText, MoreHorizontal
} from 'lucide-react';
import { PageHeader, Segmented } from '../components/ds';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) => {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

// Progression d'un trimestre vers le palier suivant (7 → 15 → 20 contrats).
const quarterProgress = (count) => {
  const milestones = [7, 15, 20];
  let prev = 0;
  for (const m of milestones) {
    if (count < m) return { pct: Math.round(((count - prev) / (m - prev)) * 100), target: m, maxed: false };
    prev = m;
  }
  return { pct: 100, target: 20, maxed: true };
};

// Progression annuelle vers le palier suivant (25 → 40 → 60 contrats).
const annualProgress = (count) => {
  const milestones = [25, 40, 60];
  let prev = 0;
  for (const m of milestones) {
    if (count < m) return { pct: Math.round(((count - prev) / (m - prev)) * 100), target: m, maxed: false };
    prev = m;
  }
  return { pct: 100, target: 60, maxed: true };
};

const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v);
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return s;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};

// Convertit une date stockée (ISO ou dd/mm/yyyy) en YYYY-MM-DD pour un <input type=date>.
const toISOInput = (v) => {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return '';
};

export default function PrimesPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // PIN gate
  const [pinVerified, setPinVerified] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Data
  const [primes, setPrimes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [allUserNames, setAllUserNames] = useState([]);
  const [customColumns, setCustomColumns] = useState([]);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});

  // Adjustment
  const [showAdjust, setShowAdjust] = useState(null);
  const [adjustCA, setAdjustCA] = useState('');
  const [showAnalyse, setShowAnalyse] = useState(false);
  const [analyse, setAnalyse] = useState(null);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  const [showCalcul, setShowCalcul] = useState(false);
  const [calculMonth, setCalculMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [calculComment, setCalculComment] = useState('');
  const [commentsMap, setCommentsMap] = useState({});
  const [calculData, setCalculData] = useState(null);
  const [loadingCalcul, setLoadingCalcul] = useState(false);
  const [emailsMap, setEmailsMap] = useState({});
  const [sendingCommercial, setSendingCommercial] = useState('');

  // Primes volume (trimestrielles / annuelles)
  const [showVolume, setShowVolume] = useState(false);
  const [volumeYear, setVolumeYear] = useState(() => new Date().getFullYear());
  const [volumeData, setVolumeData] = useState(null);
  const [loadingVolume, setLoadingVolume] = useState(false);
  const [sendingVolume, setSendingVolume] = useState('');

  // Config
  const [showConfig, setShowConfig] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [configCommerciaux, setConfigCommerciaux] = useState('');
  const [configColumns, setConfigColumns] = useState('');
  const [configEmails, setConfigEmails] = useState({});
  const [configDirection, setConfigDirection] = useState('');

  // Search
  const [search, setSearch] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Email
  const [showEmail, setShowEmail] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFilterMonth, setEmailFilterMonth] = useState('');
  const [emailFilterCommercial, setEmailFilterCommercial] = useState('');

  // Upload
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const hasPrimesAccess = isSuperAdmin && (user?.email || '').trim().toLowerCase() === 'c.perez@wasabicorner.fr';

  const verifyPin = async () => {
    setVerifying(true);
    setPinError(false);
    try {
      const res = await fetch(`${API}/api/primes/verify-pin`, {
        method: 'POST', headers, body: JSON.stringify({ pin: pinInput }),
      });
      if (res.ok) {
        setPinVerified(true);
        loadData();
      } else {
        setPinError(true);
        setPinInput('');
      }
    } catch { setPinError(true); }
    finally { setVerifying(false); }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes, cRes, colRes, uRes] = await Promise.all([
        fetch(`${API}/api/primes`, { headers }),
        fetch(`${API}/api/primes/stats`, { headers }),
        fetch(`${API}/api/primes/commerciaux`, { headers }),
        fetch(`${API}/api/primes/columns`, { headers }),
        fetch(`${API}/api/users/list`, { headers }),
      ]);
      if (pRes.ok) setPrimes(await pRes.json());
      if (sRes.ok) setStats(await sRes.json());
      if (cRes.ok) setCommerciaux(await cRes.json());
      if (colRes.ok) setCustomColumns(await colRes.json());
      if (uRes.ok) {
        const users = await uRes.json();
        setAllUserNames((users || []).map(u => (u.name || '').trim()).filter(Boolean));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  const commFrac = (c) => { const v = parseFloat(c) || 0; return v > 1 ? v / 100 : v; };
  const baseAPrimer = (ca, comm) => (parseFloat(ca) || 0) * (1 - commFrac(comm));

  const runCalcul = async (m) => {
    const month = m || calculMonth;
    setLoadingCalcul(true);
    try {
      const [rc, re] = await Promise.all([
        fetch(`${API}/api/primes/calcul-mois?month=${month}`, { headers }),
        fetch(`${API}/api/primes/emails-commerciaux`, { headers }),
      ]);
      if (rc.ok) setCalculData(await rc.json());
      if (re.ok) setEmailsMap(await re.json());
    } catch { toast.error('Erreur calcul primes'); }
    finally { setLoadingCalcul(false); }
  };

  const openCalcul = () => { setShowCalcul(true); runCalcul(); loadTemplates(); };

  // Modèles de message
  const [templates, setTemplates] = useState([]);
  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/primes/message-templates`, { headers });
      if (res.ok) setTemplates(await res.json());
    } catch { /* ignore */ }
  };
  const saveTemplate = async (content) => {
    const c = (content || '').trim();
    if (!c) { toast.error('Le message est vide'); return; }
    const name = window.prompt('Nom du modèle :');
    if (!name || !name.trim()) return;
    try {
      const res = await fetch(`${API}/api/primes/message-templates`, {
        method: 'POST', headers, body: JSON.stringify({ name: name.trim(), content: c }),
      });
      if (res.ok) { setTemplates(await res.json()); toast.success('Modèle enregistré'); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur réseau'); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    try {
      const res = await fetch(`${API}/api/primes/message-templates/${id}`, { method: 'DELETE', headers });
      if (res.ok) { setTemplates(await res.json()); toast.success('Modèle supprimé'); }
    } catch { toast.error('Erreur'); }
  };

  const deleteRecapLine = async (id) => {
    if (!window.confirm('Supprimer définitivement cette ligne des primes ? Elle ne sera plus primée.')) return;
    try {
      const res = await fetch(`${API}/api/primes/${id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Ligne supprimée'); runCalcul(); loadData(); }
      else toast.error('Erreur suppression');
    } catch { toast.error('Erreur réseau'); }
  };

  const runVolume = async (y) => {
    const year = y || volumeYear;
    setLoadingVolume(true);
    try {
      const [rv, re] = await Promise.all([
        fetch(`${API}/api/primes/volume-primes?year=${year}`, { headers }),
        fetch(`${API}/api/primes/emails-commerciaux`, { headers }),
      ]);
      if (rv.ok) setVolumeData(await rv.json());
      if (re.ok) setEmailsMap(await re.json());
    } catch { toast.error('Erreur calcul primes volume'); }
    finally { setLoadingVolume(false); }
  };

  const openVolume = () => { setShowVolume(true); runVolume(); };

  const sendVolumeEmail = async (commercial) => {
    setSendingVolume(commercial);
    try {
      const res = await fetch(`${API}/api/primes/volume-primes/email`, {
        method: 'POST', headers,
        body: JSON.stringify({ year: volumeYear, commercial }),
      });
      const d = await res.json();
      if (res.ok) toast.success(d.message || 'Email envoyé');
      else toast.error(d.detail || 'Erreur envoi');
    } catch { toast.error('Erreur envoi'); }
    finally { setSendingVolume(''); }
  };

  const sendCommercialEmail = async (commercial) => {
    setSendingCommercial(commercial);
    try {
      const res = await fetch(`${API}/api/primes/email-mois`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: calculMonth, commercial, comment: commentsMap[commercial] || calculComment }),
      });
      const d = await res.json();
      if (res.ok) toast.success(d.message || 'Email envoyé');
      else toast.error(d.detail || 'Erreur envoi');
    } catch { toast.error('Erreur envoi'); }
    finally { setSendingCommercial(''); }
  };

  // Aperçu du mail
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewCommercial, setPreviewCommercial] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [customRecipient, setCustomRecipient] = useState('');
  const [sendingPreview, setSendingPreview] = useState(false);
  const previewEmail = async (commercial) => {
    setPreviewCommercial(commercial);
    setLoadingPreview(true);
    setShowPreview(true);
    setPreviewData(null);
    setCustomRecipient('');
    try {
      const res = await fetch(`${API}/api/primes/email-mois/preview`, {
        method: 'POST', headers,
        body: JSON.stringify({ month: calculMonth, commercial, comment: commentsMap[commercial] || calculComment }),
      });
      const d = await res.json();
      if (res.ok) { setPreviewData(d); setSelectedRecipients(d.recipients || []); setPreviewSubject(d.subject || ''); setPreviewMessage(commentsMap[commercial] || calculComment || ''); }
      else { toast.error(d.detail || 'Erreur aperçu'); setShowPreview(false); }
    } catch { toast.error('Erreur réseau'); setShowPreview(false); }
    finally { setLoadingPreview(false); }
  };

  // Régénère l'aperçu avec l'objet + message personnalisés saisis
  const refreshPreview = async () => {
    try {
      const res = await fetch(`${API}/api/primes/email-mois/preview`, {
        method: 'POST', headers,
        body: JSON.stringify({ month: calculMonth, commercial: previewCommercial, comment: previewMessage, subject: previewSubject }),
      });
      const d = await res.json();
      if (res.ok) setPreviewData(prev => ({ ...prev, html: d.html, subject: d.subject }));
      else toast.error(d.detail || 'Erreur aperçu');
    } catch { toast.error('Erreur réseau'); }
  };

  // Liste des adresses déjà enregistrées (toutes commerciaux confondus)
  const savedRecipients = Array.from(new Set(
    Object.values(emailsMap || {}).flatMap(v => String(v || '').replace(/;/g, ',').split(',').map(e => e.trim()).filter(Boolean))
  )).sort();

  const toggleRecipient = (email) => {
    setSelectedRecipients(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };
  const addCustomRecipient = () => {
    const e = customRecipient.trim();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error('Adresse email invalide'); return; }
    if (!selectedRecipients.includes(e)) setSelectedRecipients(prev => [...prev, e]);
    setCustomRecipient('');
  };
  const sendFromPreview = async () => {
    if (selectedRecipients.length === 0) { toast.error('Choisissez au moins un destinataire'); return; }
    setSendingPreview(true);
    try {
      const res = await fetch(`${API}/api/primes/email-mois`, {
        method: 'POST', headers,
        body: JSON.stringify({ month: calculMonth, commercial: previewCommercial, comment: previewMessage, subject: previewSubject, recipients: selectedRecipients }),
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || 'Email envoyé'); setShowPreview(false); }
      else toast.error(d.detail || 'Erreur envoi');
    } catch { toast.error('Erreur réseau'); }
    finally { setSendingPreview(false); }
  };

  const exportVolume = async () => {
    try {
      const res = await fetch(`${API}/api/primes/export-volume?year=${volumeYear}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Erreur export'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `primes_volume_${volumeYear}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch { toast.error('Erreur export'); }
  };

  const openAnalyse = async () => {
    setShowAnalyse(true);
    setLoadingAnalyse(true);
    try {
      const res = await fetch(`${API}/api/primes/analyse-12mois`, { headers });
      if (res.ok) setAnalyse(await res.json());
    } catch { toast.error('Erreur chargement analyse'); }
    finally { setLoadingAnalyse(false); }
  };

  const resetForm = () => {
    setForm({ commercial: '', client: '', type_contrat: 'nouveau', ca_previsionnel: '', commission_pct: '', date_signature: '', date_mise_en_place: '', ca_12_mois: '', ajustement_prime: '' });
    setEditId(null);
  };

  const handleSave = async () => {
    const base = baseAPrimer(form.ca_previsionnel, form.commission_pct);
    const payload = {
      commercial: form.commercial,
      client: form.client,
      type_contrat: form.type_contrat || 'nouveau',
      ca_previsionnel: parseFloat(form.ca_previsionnel) || 0,
      commission_pct: parseFloat(form.commission_pct) || 0,
      date_signature: form.date_signature || '',
      date_mise_en_place: form.date_mise_en_place || '',
      montant_prime: Math.round(base * 0.01 * 100) / 100,
      ca_12_mois: form.ca_12_mois ? parseFloat(form.ca_12_mois) : null,
      ajustement_prime: form.ajustement_prime ? parseFloat(form.ajustement_prime) : null,
    };
    try {
      const url = editId ? `${API}/api/primes/${editId}` : `${API}/api/primes`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editId ? 'Prime modifiée' : 'Prime ajoutée');
        setShowForm(false);
        resetForm();
        loadData();
        if (showCalcul) runCalcul(calculMonth);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prime ?')) return;
    try {
      await fetch(`${API}/api/primes/${id}`, { method: 'DELETE', headers });
      toast.success('Prime supprimée');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.length} prime(s) sélectionnée(s) ? Cette action est irréversible.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/api/primes/bulk-delete`, {
        method: 'POST', headers, body: JSON.stringify({ ids: selectedIds }),
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || 'Primes supprimées'); setSelectedIds([]); loadData(); }
      else toast.error(d.detail || 'Erreur');
    } catch { toast.error('Erreur réseau'); }
    finally { setBulkDeleting(false); }
  };

  const [cleaningDups, setCleaningDups] = useState(false);
  const handleRemoveDuplicates = async () => {
    setCleaningDups(true);
    try {
      const chk = await fetch(`${API}/api/primes/duplicates`, { headers });
      const info = await chk.json();
      if (!chk.ok) { toast.error(info.detail || 'Erreur'); return; }
      if (!info.duplicates) { toast.success('Aucun doublon trouvé 🎉'); return; }
      if (!window.confirm(`${info.duplicates} doublon(s) détecté(s) (sur ${info.total} lignes). Supprimer les doublons en gardant la 1re occurrence de chaque contrat ?`)) return;
      const res = await fetch(`${API}/api/primes/remove-duplicates`, { method: 'POST', headers });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || 'Doublons supprimés'); loadData(); }
      else toast.error(d.detail || 'Erreur');
    } catch { toast.error('Erreur réseau'); }
    finally { setCleaningDups(false); }
  };

  // Aperçu + édition avant envoi groupé (commerciaux / direction)
  const [bulk, setBulk] = useState({ open: false, mode: 'commerciaux', loading: false, sending: false, subject: '', message: '', html: '', recipients: '', total: 0, sampleName: '', attachment: '' });

  const firstCommercialWithLines = () => {
    const g = (calculData?.commerciaux || []).find(x => x.nouveaux.length || x.renouvellements.length || x.deltas.length || (x.trimestrielles || []).length);
    return g?.commercial || null;
  };

  const openBulkCommerciaux = async () => {
    const sample = firstCommercialWithLines();
    if (!sample) { toast.warning('Aucune prime à envoyer ce mois-ci.'); return; }
    setBulk(b => ({ ...b, open: true, mode: 'commerciaux', loading: true, subject: '', message: calculComment || '', html: '', sampleName: sample, attachment: '' }));
    try {
      const res = await fetch(`${API}/api/primes/email-mois/preview`, {
        method: 'POST', headers, body: JSON.stringify({ month: calculMonth, commercial: sample, comment: calculComment || '' }),
      });
      const d = await res.json();
      if (res.ok) setBulk(b => ({ ...b, loading: false, html: d.html, subject: d.default_subject || d.subject || '', attachment: (d.attachments || [d.attachment_name]).filter(Boolean).join(', ') }));
      else { setBulk(b => ({ ...b, loading: false })); toast.error(d.detail || 'Erreur aperçu'); }
    } catch { setBulk(b => ({ ...b, loading: false })); toast.error('Erreur réseau'); }
  };

  const openBulkDirection = async () => {
    const sample = firstCommercialWithLines();
    if (!sample) { toast.warning('Aucune prime à envoyer ce mois-ci.'); return; }
    setBulk(b => ({ ...b, open: true, mode: 'direction', loading: true, subject: '', message: calculComment || '', html: '', attachment: '' }));
    try {
      const res = await fetch(`${API}/api/primes/email-mois/direction/preview`, {
        method: 'POST', headers, body: JSON.stringify({ month: calculMonth, comment: calculComment || '' }),
      });
      const d = await res.json();
      if (res.ok) setBulk(b => ({ ...b, loading: false, html: d.html, subject: d.default_subject || d.subject || '', recipients: (d.recipients || []).join(', '), total: d.total, attachment: d.attachment_name || '' }));
      else { setBulk(b => ({ ...b, loading: false })); toast.error(d.detail || 'Erreur aperçu'); }
    } catch { setBulk(b => ({ ...b, loading: false })); toast.error('Erreur réseau'); }
  };

  const refreshBulkPreview = async () => {
    setBulk(b => ({ ...b, loading: true }));
    try {
      const url = bulk.mode === 'direction' ? `${API}/api/primes/email-mois/direction/preview` : `${API}/api/primes/email-mois/preview`;
      const body = bulk.mode === 'direction'
        ? { month: calculMonth, comment: bulk.message, subject: bulk.subject }
        : { month: calculMonth, commercial: bulk.sampleName, comment: bulk.message, subject: bulk.subject };
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await res.json();
      if (res.ok) setBulk(b => ({ ...b, loading: false, html: d.html }));
      else { setBulk(b => ({ ...b, loading: false })); toast.error(d.detail || 'Erreur'); }
    } catch { setBulk(b => ({ ...b, loading: false })); toast.error('Erreur réseau'); }
  };

  const sendBulk = async () => {
    if (bulk.mode === 'direction') {
      const recips = bulk.recipients.split(',').map(s => s.trim()).filter(Boolean);
      if (recips.length === 0) { toast.error('Ajoutez au moins une adresse Direction.'); return; }
      if (!window.confirm(`Envoyer le récap consolidé à : ${recips.join(', ')} ?`)) return;
      setBulk(b => ({ ...b, sending: true }));
      try {
        const res = await fetch(`${API}/api/primes/email-mois/direction`, {
          method: 'POST', headers, body: JSON.stringify({ month: calculMonth, subject: bulk.subject, comment: bulk.message, recipients: recips }),
        });
        const d = await res.json();
        if (res.ok) { toast.success(d.message || 'Récap Direction envoyé'); setBulk(b => ({ ...b, open: false, sending: false })); }
        else { toast.error(d.detail || 'Erreur'); setBulk(b => ({ ...b, sending: false })); }
      } catch { toast.error('Erreur réseau'); setBulk(b => ({ ...b, sending: false })); }
    } else {
      if (!window.confirm('Envoyer le récap du mois à TOUS les commerciaux ayant une adresse configurée ?')) return;
      setBulk(b => ({ ...b, sending: true }));
      try {
        const res = await fetch(`${API}/api/primes/email-mois-all`, {
          method: 'POST', headers, body: JSON.stringify({ month: calculMonth, subject: bulk.subject, comment: bulk.message, comments: commentsMap }),
        });
        const d = await res.json();
        if (res.ok) {
          toast.success(d.message || 'Emails envoyés');
          if (d.skipped?.length) toast.warning(`Sans adresse : ${d.skipped.join(', ')}`);
          if (d.errors?.length) toast.error(`Erreurs : ${d.errors.join(' · ')}`);
          setBulk(b => ({ ...b, open: false, sending: false }));
        } else { toast.error(d.detail || 'Erreur'); setBulk(b => ({ ...b, sending: false })); }
      } catch { toast.error('Erreur réseau'); setBulk(b => ({ ...b, sending: false })); }
    }
  };

  // Historique des envois
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const openHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/api/primes/email-history?limit=200`, { headers });
      if (res.ok) setHistoryData(await res.json());
    } catch { toast.error('Erreur historique'); }
    finally { setLoadingHistory(false); }
  };

  // Conditions PDF éditables
  const [showConditions, setShowConditions] = useState(false);
  const [conditionsText, setConditionsText] = useState('');
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [savingConditions, setSavingConditions] = useState(false);
  const openConditions = async () => {
    setShowConditions(true);
    setLoadingConditions(true);
    try {
      const res = await fetch(`${API}/api/primes/conditions`, { headers });
      if (res.ok) { const d = await res.json(); setConditionsText(d.content || ''); }
    } catch { toast.error('Erreur chargement conditions'); }
    finally { setLoadingConditions(false); }
  };
  const saveConditions = async () => {
    setSavingConditions(true);
    try {
      const res = await fetch(`${API}/api/primes/conditions`, { method: 'PUT', headers, body: JSON.stringify({ content: conditionsText }) });
      if (res.ok) toast.success('Conditions enregistrées');
      else toast.error('Erreur');
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingConditions(false); }
  };
  const previewConditionsPdf = async () => {
    try {
      const res = await fetch(`${API}/api/primes/conditions/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Erreur aperçu PDF'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Erreur'); }
  };

  const openEdit = (p) => {
    setForm({
      commercial: p.commercial || '',
      client: p.client || '',
      type_contrat: p.type_contrat || 'nouveau',
      ca_previsionnel: p.ca_previsionnel || '',
      commission_pct: p.commission_pct || '',
      date_signature: toISOInput(p.date_signature),
      date_mise_en_place: toISOInput(p.date_mise_en_place),
      ca_12_mois: p.ca_12_mois || '',
      ajustement_prime: p.ajustement_prime || '',
    });
    setEditId(p.id);
    setShowForm(true);
  };

  // Date par défaut (mois d'ouverture M-1) pour un ajout manuel depuis le récap
  const openingDefaultDate = () => {
    const [y, m] = calculMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    return d.toISOString().slice(0, 10);
  };
  // "+ Ligne" : ouvre le formulaire pré-rempli (affecter un dossier à un commercial)
  const openAddPrimeFor = (commercial) => {
    const dt = openingDefaultDate();
    setForm({ commercial: commercial || '', client: '', type_contrat: 'nouveau', ca_previsionnel: '', commission_pct: '', date_signature: dt, date_mise_en_place: dt, ca_12_mois: '', ajustement_prime: '' });
    setEditId(null);
    setShowForm(true);
  };
  // Édition inline du CA / taux dans le récap mensuel -> recalcule la prime
  const updateRecapField = async (r, patch) => {
    try {
      const res = await fetch(`${API}/api/primes/${r.id}`, { method: 'PUT', headers, body: JSON.stringify(patch) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
      toast.success('Montant mis à jour');
      await runCalcul(calculMonth);
      loadData();
    } catch { toast.error('Erreur réseau'); }
  };

  const handleAdjust = async () => {
    if (!showAdjust || !adjustCA) return;
    const ca12 = parseFloat(adjustCA);
    const comm = commFrac(showAdjust.commission_pct);
    const primeDue = ca12 * (1 - comm) * 0.01;
    const alreadyPaid = showAdjust.prime_immediate || 0;
    const ajustement = Math.round((primeDue - alreadyPaid) * 100) / 100;

    try {
      await fetch(`${API}/api/primes/${showAdjust.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ca_12_mois: ca12, ajustement_prime: ajustement }),
      });
      toast.success('Ajustement enregistré');
      setShowAdjust(null);
      setAdjustCA('');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/primes/import-excel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur import');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/api/primes/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `primes_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export'); }
  };

  const handleSendEmail = async () => {
    const recipients = emailRecipients.split(/[,;\n]+/).map(e => e.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error('Saisissez au moins un destinataire'); return; }
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/api/primes/send-email`, {
        method: 'POST', headers,
        body: JSON.stringify({
          recipients,
          subject: emailSubject || undefined,
          message: emailMessage || '',
          filter_month: emailFilterMonth || null,
          filter_commercial: emailFilterCommercial || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowEmail(false);
        setEmailRecipients(''); setEmailSubject(''); setEmailMessage('');
        setEmailFilterMonth(''); setEmailFilterCommercial('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur envoi');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setSendingEmail(false); }
  };

  const handleSaveConfig = async () => {
    const names = configCommerciaux.split('\n').map(s => s.trim()).filter(Boolean);
    const cols = configColumns.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      await Promise.all([
        fetch(`${API}/api/primes/commerciaux`, { method: 'PUT', headers, body: JSON.stringify({ names }) }),
        fetch(`${API}/api/primes/columns`, { method: 'PUT', headers, body: JSON.stringify({ columns: cols }) }),
        fetch(`${API}/api/primes/emails-commerciaux`, { method: 'PUT', headers, body: JSON.stringify({ mapping: configEmails }) }),
        fetch(`${API}/api/primes/emails-direction`, { method: 'PUT', headers, body: JSON.stringify({ emails: configDirection }) }),
      ]);
      toast.success('Configuration sauvegardée');
      setShowConfig(false);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const exportMois = async () => {
    try {
      const res = await fetch(`${API}/api/primes/export-mois?month=${calculMonth}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Erreur export'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `primes_${calculMonth}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch { toast.error('Erreur export'); }
  };

  const filtered = primes.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.commercial || '').toLowerCase().includes(q) || (p.client || '').toLowerCase().includes(q);
  });

  const needsReviewCount = primes.filter(p => p.needs_review).length;

  // PIN Screen
  if (!hasPrimesAccess) {
    return <AppLayout><div className="flex items-center justify-center h-[60vh]"><Card className="w-80 text-center"><CardContent className="p-8"><Shield className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="font-semibold">Accès restreint</p><p className="text-sm text-slate-500 mt-1">Espace réservé à la Direction Commerciale</p></CardContent></Card></div></AppLayout>;
  }

  if (!pinVerified) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="w-96 border-2 border-slate-300" data-testid="pin-gate">
            <CardContent className="p-8 text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-slate-900 mx-auto flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Traitement des Primes</h2>
                <p className="text-sm text-slate-500 mt-1">Entrez le code secret pour accéder</p>
              </div>
              <div className="flex justify-center gap-2">
                {[0, 1, 2, 3].map(i => (
                  <input
                    key={i}
                    type="password"
                    maxLength={1}
                    className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg ${pinError ? 'border-red-400 bg-red-50' : 'border-slate-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
                    value={pinInput[i] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!/^\d?$/.test(val)) return;
                      const newPin = pinInput.split('');
                      newPin[i] = val;
                      const joined = newPin.join('').slice(0, 4);
                      setPinInput(joined);
                      setPinError(false);
                      if (val && i < 3) {
                        const next = e.target.parentElement.children[i + 1];
                        if (next) next.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !pinInput[i] && i > 0) {
                        const prev = e.target.parentElement.children[i - 1];
                        if (prev) prev.focus();
                      }
                      if (e.key === 'Enter' && pinInput.length === 4) verifyPin();
                    }}
                    data-testid={`pin-digit-${i}`}
                  />
                ))}
              </div>
              {pinError && <p className="text-red-500 text-sm">Code incorrect</p>}
              <Button onClick={verifyPin} disabled={pinInput.length !== 4 || verifying} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="pin-submit">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Accéder
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Main content
  return (
    <AppLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="primes-page">
        {/* En-tête §5.5.1 */}
        <PageHeader overline="Commerce · Calcul et versement des primes commerciales" title="PRIMES">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1" data-testid="import-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} <span className="hidden sm:inline">Importer</span>
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={handleExport} className="gap-1" data-testid="export-btn">
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Exporter Excel</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
            const now = new Date();
            const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const monthStr = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
            setEmailFilterMonth(monthStr);
            setEmailFilterCommercial('');
            setEmailSubject(`Primes ${prevMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} — Export du ${now.toLocaleDateString('fr-FR')}`);
            setShowEmail(true);
          }} className="gap-1" data-testid="email-btn">
            <Mail className="w-4 h-4" /> <span className="hidden sm:inline">Envoyer par e-mail</span>
          </Button>
          {/* Menu « Plus » §5.5.1 */}
          <div className="relative">
            <Button variant="outline" size="sm" aria-label="Plus d'actions" onClick={() => setShowMore(v => !v)} className="gap-1" data-testid="more-btn">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            {showMore && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMore(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 w-56 rounded-ctrl bg-surface border border-border shadow-xl py-1" data-testid="more-menu">
                  <button onClick={async () => {
                    setShowMore(false);
                    setConfigCommerciaux(commerciaux.join('\n'));
                    setConfigColumns(customColumns.join('\n'));
                    try {
                      const r = await fetch(`${API}/api/primes/emails-commerciaux`, { headers });
                      if (r.ok) setConfigEmails(await r.json());
                      const rd = await fetch(`${API}/api/primes/emails-direction`, { headers });
                      if (rd.ok) setConfigDirection((await rd.json()).emails || '');
                    } catch { /* ignore */ }
                    setShowConfig(true);
                  }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-2" data-testid="config-btn"><Settings className="w-4 h-4 text-text-2" /> Configuration</button>
                  <button onClick={() => { setShowMore(false); openConditions(); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-2" data-testid="conditions-btn"><FileText className="w-4 h-4 text-text-2" /> Conditions PDF</button>
                  <button onClick={() => { setShowMore(false); openAnalyse(); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-2" data-testid="analyse-btn"><BarChart3 className="w-4 h-4 text-text-2" /> Analyse 12 mois</button>
                  <button onClick={() => { setShowMore(false); handleRemoveDuplicates(); }} disabled={cleaningDups} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-surface-2" data-testid="dedup-btn">{cleaningDups ? <Loader2 className="w-4 h-4 animate-spin" /> : <CopyX className="w-4 h-4" />} Nettoyer les doublons</button>
                </div>
              </>
            )}
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="gap-1" data-testid="add-prime-btn">
            <Plus className="w-4 h-4" /> Nouvelle prime
          </Button>
        </PageHeader>

        {/* Bloc scène graphite §5.5.2 : indicateurs + règle de calcul */}
        {stats && (
          <div className="rounded-scene bg-graphite-900 p-6 sm:p-7 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6" data-testid="primes-scene">
            <div className="grid grid-cols-1 sm:grid-cols-3">
              <div className="px-5 first:pl-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">Primes immédiates</p>
                <p className="font-display font-semibold text-white text-[40px] leading-none tabular-nums">{fmtCurrency(stats.total_prime_immediate)}</p>
                <p className="text-[13px] text-ondark-2 mt-1.5">Nouveaux contrats · 0,75 %</p>
              </div>
              <div className="px-5 border-t sm:border-t-0 sm:border-l border-graphite-600 pt-4 sm:pt-0 mt-4 sm:mt-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">Primes mensuelles</p>
                <p className="font-display font-semibold text-white text-[40px] leading-none tabular-nums">{fmtCurrency(stats.total_prime_mensuelle)}</p>
                <p className="text-[13px] text-ondark-2 mt-1.5">Renouvellements · 1 %</p>
              </div>
              <div className="px-5 border-t sm:border-t-0 sm:border-l border-graphite-600 pt-4 sm:pt-0 mt-4 sm:mt-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">Base à primer</p>
                <p className="font-display font-semibold text-white text-[40px] leading-none tabular-nums">{fmtCurrency(stats.total_base_a_primer)}</p>
                <p className="text-[13px] text-ondark-2 mt-1.5">Sur {stats.total} dossier{stats.total > 1 ? 's' : ''}{needsReviewCount > 0 ? ` · ${needsReviewCount} à vérifier (12 mois)` : ''}</p>
              </div>
            </div>
            <div className="lg:border-l border-graphite-600 lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-graphite-600 lg:max-w-[340px]">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange mb-2">Règle de calcul</p>
              <ul className="space-y-1.5 text-[13px] text-ondark-2 leading-snug">
                <li><span className="text-white font-semibold">Base</span> = CA prévisionnel moins la commission client.</li>
                <li><span className="text-white font-semibold">Nouveau contrat</span> : 0,75 % versé à la signature, régularisé à 1 % après 12 mois de CA réel.</li>
                <li><span className="text-white font-semibold">Renouvellement</span> : 1 % de la moyenne mensuelle, versé chaque mois.</li>
              </ul>
            </div>
          </div>
        )}

        {/* Sélecteur segmenté §5.5.3 + recherche */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex items-center gap-0.5 rounded-ctrl bg-segmented p-1" data-testid="primes-views">
            <button onClick={openCalcul} data-testid="calcul-btn" className="h-9 px-4 rounded-[7px] text-[13px] font-semibold text-text-2 hover:text-ink transition-colors">Primes du mois</button>
            <button onClick={openVolume} data-testid="volume-btn" className="h-9 px-4 rounded-[7px] text-[13px] font-semibold text-text-2 hover:text-ink transition-colors">Trimestrielles &amp; annuelles</button>
            <button onClick={openHistory} data-testid="history-btn" className="h-9 px-4 rounded-[7px] text-[13px] font-semibold text-text-2 hover:text-ink transition-colors">Historique des envois</button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 bg-surface border border-border rounded-ctrl px-3 py-1.5" data-testid="bulk-delete-bar">
                <span className="text-sm text-text-2 font-medium">{selectedIds.length} sélectionnée(s)</span>
                <Button size="sm" variant="ghost" className="h-7 text-text-3" onClick={() => setSelectedIds([])} data-testid="bulk-clear-btn">Annuler</Button>
                <Button size="sm" onClick={handleBulkDelete} disabled={bulkDeleting} className="h-7 gap-1" style={{ background: '#B42318' }} data-testid="bulk-delete-btn">
                  {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Supprimer
                </Button>
              </div>
            )}
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
              <Input placeholder="Rechercher par commercial ou client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-primes" />
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange" /></div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-surface-2">
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-red-600 cursor-pointer"
                          checked={filtered.length > 0 && filtered.every(p => selectedIds.includes(p.id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedIds(prev => [...new Set([...prev, ...filtered.map(p => p.id)])]);
                            else { const fset = new Set(filtered.map(p => p.id)); setSelectedIds(prev => prev.filter(id => !fset.has(id))); }
                          }}
                          data-testid="select-all-primes"
                        />
                      </TableHead>
                      <TableHead>Commercial</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead className="text-right">CA Prévi.</TableHead>
                      <TableHead className="text-center">% Com.</TableHead>
                      <TableHead className="text-right">Base à primer</TableHead>
                      <TableHead>Date signature contrat</TableHead>
                      <TableHead>Date prévisionnelle ouverture</TableHead>
                      <TableHead className="text-right">Prime immédiate</TableHead>
                      <TableHead className="text-right">CA 12 Mois</TableHead>
                      <TableHead className="text-right">Ajustement / Delta</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={14} className="text-center py-8 text-slate-400">Aucune prime</TableCell></TableRow>
                    ) : filtered.map(p => (
                      <TableRow key={p.id} className={selectedIds.includes(p.id) ? 'bg-surface-2' : p.needs_review ? 'bg-orange-tint/40' : p.review_overdue ? 'bg-orange-tint/60' : ''} data-testid={`prime-row-${p.id}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-red-600 cursor-pointer"
                            checked={selectedIds.includes(p.id)}
                            onChange={() => toggleSelect(p.id)}
                            data-testid={`select-prime-${p.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-sm">{p.commercial}</TableCell>
                        <TableCell className="text-sm">{p.client}</TableCell>
                        <TableCell className="text-center">
                          {p.type_contrat === 'renouvellement'
                            ? <Badge className="bg-surface-2 text-[#2E2D2A] text-[10px]">Renouv.</Badge>
                            : <Badge className="bg-orange-tint text-orange-tint-text text-[10px]">Nouveau</Badge>}
                        </TableCell>
                        <TableCell className="text-right font-medium">{fmtCurrency(p.ca_previsionnel)}</TableCell>
                        <TableCell className="text-center">{p.commission_pct}%</TableCell>
                        <TableCell className="text-right text-slate-600">{fmtCurrency(p.base_a_primer)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(p.date_signature)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(p.date_mise_en_place)}</TableCell>
                        <TableCell className="text-right text-ink font-semibold">
                          {p.type_contrat === 'renouvellement'
                            ? <span>{fmtCurrency(p.prime_mensuelle)}<span className="text-[10px] text-slate-400">/mois</span></span>
                            : fmtCurrency(p.prime_immediate)}
                        </TableCell>
                        <TableCell className="text-right">{p.type_contrat === 'renouvellement' ? <span className="text-slate-300">—</span> : (p.ca_12_mois ? fmtCurrency(p.ca_12_mois) : (
                          p.needs_review ? (
                            <Button size="sm" variant="outline" className="text-orange-text border-orange gap-1 h-7" onClick={() => { setShowAdjust(p); setAdjustCA(''); }} data-testid={`adjust-btn-${p.id}`}>
                              <AlertTriangle className="w-3 h-3" /> Saisir
                            </Button>
                          ) : <span className="text-slate-400">En attente</span>
                        ))}</TableCell>
                        <TableCell className="text-right">{p.type_contrat === 'renouvellement' ? <span className="text-slate-300">—</span> : (p.ajustement != null ? (
                          <Badge className={`${p.ajustement > 0 ? 'bg-surface-2 text-[#2E2D2A]' : p.ajustement < 0 ? 'text-white' : 'bg-surface-2 text-text-3'} text-xs`} style={p.ajustement < 0 ? { background: '#B42318' } : undefined}>
                            {p.ajustement > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : p.ajustement < 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : null}
                            {p.ajustement > 0 ? '+' : ''}{fmtCurrency(p.ajustement)}
                          </Badge>
                        ) : '—')}</TableCell>
                        <TableCell className="text-center">
                          {p.type_contrat === 'renouvellement' ? (
                            <Badge className="bg-surface-2 text-[#2E2D2A] text-[10px]">Mensuel</Badge>
                          ) : p.needs_review ? (
                            <Badge className="bg-orange-tint text-orange-tint-text text-[10px] gap-1"><Clock className="w-3 h-3" /> 12M+</Badge>
                          ) : p.ca_12_mois ? (
                            <Badge className="bg-graphite-900 text-white text-[10px] gap-1"><CheckCircle2 className="w-3 h-3" /> Ajusté</Badge>
                          ) : (
                            <Badge className="bg-surface-2 text-[#2E2D2A] text-[10px]">{p.months_elapsed || 0} mois</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label="Modifier" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 text-text-2" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label="Supprimer" onClick={() => handleDelete(p.id)}><Trash2 className="w-3.5 h-3.5 text-danger" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-lg" data-testid="prime-form">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">{editId ? 'Modifier la prime' : 'Nouvelle prime'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom du commercial *</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.commercial} onChange={e => setForm({ ...form, commercial: e.target.value })} data-testid="form-commercial">
                <option value="">Sélectionner</option>
                {[...new Set([...(form.commercial ? [form.commercial] : []), ...commerciaux, ...allUserNames])].sort((a, b) => a.localeCompare(b, 'fr')).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Client (Enseigne + Ville) *</Label>
              <Input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="Ex: Leclerc Bruges" data-testid="form-client" />
            </div>
            <div>
              <Label>Type de contrat *</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.type_contrat} onChange={e => setForm({ ...form, type_contrat: e.target.value })} data-testid="form-type">
                <option value="nouveau">Nouveau contrat (0,75% + régul. 12 mois)</option>
                <option value="renouvellement">Renouvellement (1% mensuel)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CA Prévisionnel *</Label>
                <Input type="number" value={form.ca_previsionnel} onChange={e => setForm({ ...form, ca_previsionnel: e.target.value })} placeholder="500000" data-testid="form-ca" />
              </div>
              <div>
                <Label>% Commission client</Label>
                <Input type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: e.target.value })} placeholder="18" data-testid="form-commission" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date de signature contrat</Label>
                <Input type="date" value={form.date_signature || ''} onChange={e => setForm({ ...form, date_signature: e.target.value })} data-testid="form-date-signature" />
              </div>
              <div>
                <Label>Date prévisionnelle d'ouverture</Label>
                <Input type="date" value={form.date_mise_en_place} onChange={e => setForm({ ...form, date_mise_en_place: e.target.value })} data-testid="form-date" />
              </div>
            </div>
            {form.ca_previsionnel && (() => {
              const base = baseAPrimer(form.ca_previsionnel, form.commission_pct);
              return (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 space-y-1">
                <p className="text-sm"><strong>Base à primer</strong> (CA − commission {form.commission_pct || 0}%) : <strong>{fmtCurrency(base)}</strong></p>
                {form.type_contrat === 'renouvellement' ? (
                  <p className="text-sm text-violet-700"><strong>Prime mensuelle (1% de la moyenne) :</strong> {fmtCurrency((base / 12) * 0.01)}/mois</p>
                ) : (
                  <>
                    <p className="text-sm text-emerald-700"><strong>Versement immédiat (0,75%) :</strong> {fmtCurrency(base * 0.0075)}</p>
                    <p className="text-sm text-blue-600"><strong>Régularisation à 12 mois → cible 1% :</strong> {fmtCurrency(base * 0.01)}</p>
                  </>
                )}
              </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CA 12 Mois (si connu)</Label>
                <Input type="number" value={form.ca_12_mois} onChange={e => setForm({ ...form, ca_12_mois: e.target.value })} placeholder="Après 12 mois" data-testid="form-ca12" />
              </div>
              <div>
                <Label>Ajustement Prime</Label>
                <Input type="number" value={form.ajustement_prime} onChange={e => setForm({ ...form, ajustement_prime: e.target.value })} placeholder="Calculé auto" data-testid="form-ajust" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Annuler</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-prime-btn">
              {editId ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog open={!!showAdjust} onOpenChange={v => { if (!v) setShowAdjust(null); }}>
        <DialogContent className="max-w-md" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Ajustement 12 mois
            </DialogTitle>
          </DialogHeader>
          {showAdjust && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
                <p><strong>Commercial :</strong> {showAdjust.commercial}</p>
                <p><strong>Client :</strong> {showAdjust.client}</p>
                <p><strong>CA Prévisionnel :</strong> {fmtCurrency(showAdjust.ca_previsionnel)} (commission {showAdjust.commission_pct}%)</p>
                <p><strong>Base à primer :</strong> {fmtCurrency(showAdjust.base_a_primer)}</p>
                <p><strong>Prime déjà versée (0,75%) :</strong> {fmtCurrency(showAdjust.prime_immediate)}</p>
              </div>
              <div>
                <Label>CA réel des 12 derniers mois *</Label>
                <Input type="number" value={adjustCA} onChange={e => setAdjustCA(e.target.value)} placeholder="Saisir le CA réel" autoFocus data-testid="adjust-ca-input" />
              </div>
              {adjustCA && (() => {
                const comm = commFrac(showAdjust.commission_pct);
                const primeDue = parseFloat(adjustCA) * (1 - comm) * 0.01;
                const paid = showAdjust.prime_immediate || 0;
                const diff = Math.round((primeDue - paid) * 100) / 100;
                return (
                <div className={`p-4 rounded-lg border-2 ${diff > 0 ? 'bg-emerald-50 border-emerald-300' : diff < 0 ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-300'}`}>
                  <p className="text-sm text-slate-600">Prime totale due (1% du réel après commission) : <strong>{fmtCurrency(primeDue)}</strong></p>
                  <p className="text-sm text-slate-600">Déjà versé (0,75%) : <strong>{fmtCurrency(paid)}</strong></p>
                  <hr className="my-2" />
                  <p className={`text-lg font-bold ${diff > 0 ? 'text-emerald-700' : diff < 0 ? 'text-red-700' : 'text-slate-700'}`}>
                    {diff > 0 ? (
                      <><TrendingUp className="w-5 h-5 inline mr-1" /> À verser : +{fmtCurrency(diff)}</>
                    ) : diff < 0 ? (
                      <><TrendingDown className="w-5 h-5 inline mr-1" /> À récupérer : {fmtCurrency(diff)}</>
                    ) : 'Aucun ajustement nécessaire'}
                  </p>
                </div>
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjust(null)}>Annuler</Button>
            <Button onClick={handleAdjust} disabled={!adjustCA} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-adjust-btn">
              Enregistrer l'ajustement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Noms des commerciaux (un par ligne)</Label>
              <textarea className="w-full min-h-[100px] p-2 border rounded-lg text-sm" value={configCommerciaux} onChange={e => setConfigCommerciaux(e.target.value)} data-testid="config-commerciaux" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Mail className="w-4 h-4 text-emerald-600" /> Emails d'envoi par commercial</Label>
              <p className="text-xs text-slate-400 mb-2">Adresse(s) qui recevront l'Excel des primes (plusieurs séparées par une virgule).</p>
              <div className="space-y-2">
                {configCommerciaux.split('\n').map(s => s.trim()).filter(Boolean).map(name => (
                  <div key={name} className="flex flex-col sm:flex-row sm:items-center gap-1.5" data-testid={`config-email-row-${name}`}>
                    <span className="sm:w-40 text-sm font-medium text-slate-700 shrink-0">{name}</span>
                    <Input type="text" placeholder="email1@wasabi.fr, email2@..." value={configEmails[name] || ''} onChange={e => setConfigEmails(prev => ({ ...prev, [name]: e.target.value }))} className="flex-1 h-9" data-testid={`config-email-input-${name}`} />
                  </div>
                ))}
                {configCommerciaux.split('\n').map(s => s.trim()).filter(Boolean).length === 0 && (
                  <p className="text-xs text-slate-400">Ajoutez d'abord des commerciaux ci-dessus.</p>
                )}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Mail className="w-4 h-4 text-blue-600" /> Emails de la Direction</Label>
              <p className="text-xs text-slate-400 mb-2">Destinataires du récap consolidé (tous commerciaux). Plusieurs adresses séparées par une virgule.</p>
              <Input type="text" placeholder="direction@wasabi.fr, daf@wasabi.fr, ..." value={configDirection} onChange={e => setConfigDirection(e.target.value)} className="h-9" data-testid="config-direction-input" />
            </div>
            <div>
              <Label>Colonnes personnalisées (un nom par ligne)</Label>
              <textarea className="w-full min-h-[80px] p-2 border rounded-lg text-sm" value={configColumns} onChange={e => setConfigColumns(e.target.value)} placeholder="Ex: Commentaires&#10;Type de contrat" data-testid="config-columns" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Annuler</Button>
            <Button onClick={handleSaveConfig} data-testid="save-config-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Email Dialog */}
      <Dialog open={showEmail} onOpenChange={v => { if (!v) { setShowEmail(false); setEmailFilterMonth(''); setEmailFilterCommercial(''); } }}>
        <DialogContent className="max-w-lg" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Mail className="w-5 h-5" />
              Envoyer les primes par email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Filters */}
            <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
              <p className="text-xs font-semibold text-slate-600 uppercase">Filtres d'envoi</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mois d'activité</Label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={emailFilterMonth} onChange={e => setEmailFilterMonth(e.target.value)} data-testid="email-filter-month">
                    <option value="">Tous les mois</option>
                    {(() => {
                      const months = new Set();
                      primes.forEach(p => {
                        if (p.date_mise_en_place) {
                          const d = new Date(p.date_mise_en_place);
                          months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                        }
                      });
                      // Also add last 12 months
                      const now = new Date();
                      for (let i = 0; i < 12; i++) {
                        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                      }
                      return [...months].sort().reverse().map(m => {
                        const [y, mo] = m.split('-');
                        const label = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                        return <option key={m} value={m}>{label}</option>;
                      });
                    })()}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Commercial</Label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={emailFilterCommercial} onChange={e => setEmailFilterCommercial(e.target.value)} data-testid="email-filter-commercial">
                    <option value="">Tous les commerciaux</option>
                    {commerciaux.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {/* Preview */}
              {(() => {
                let preview = primes;
                if (emailFilterMonth) {
                  preview = preview.filter(p => {
                    if (!p.date_mise_en_place) return false;
                    const d = new Date(p.date_mise_en_place);
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === emailFilterMonth;
                  });
                }
                if (emailFilterCommercial) {
                  preview = preview.filter(p => p.commercial === emailFilterCommercial);
                }
                const totalCA = preview.reduce((s, p) => s + (p.ca_previsionnel || 0), 0);
                const totalPrime = Math.round(totalCA * 0.0075 * 100) / 100;
                return (
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <Badge variant="secondary">{preview.length} prime{preview.length !== 1 ? 's' : ''}</Badge>
                    <span className="text-slate-500">CA: <strong>{fmtCurrency(totalCA)}</strong></span>
                    <span className="text-emerald-600">Prime 0.75%: <strong>{fmtCurrency(totalPrime)}</strong></span>
                  </div>
                );
              })()}
            </div>

            <div>
              <Label>Destinataire(s) * <span className="text-xs text-slate-400">(séparer par virgule ou saut de ligne)</span></Label>
              <textarea
                className="w-full min-h-[70px] p-3 border rounded-lg text-sm"
                placeholder={"destinataire1@email.com\ndestinataire2@email.com"}
                value={emailRecipients}
                onChange={e => setEmailRecipients(e.target.value)}
                data-testid="email-recipients"
              />
            </div>
            <div>
              <Label>Objet</Label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} data-testid="email-subject" />
            </div>
            <div>
              <Label>Message (optionnel)</Label>
              <textarea
                className="w-full min-h-[50px] p-3 border rounded-lg text-sm"
                placeholder="Bonjour, veuillez trouver ci-joint..."
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                data-testid="email-message"
              />
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
              <Download className="w-4 h-4" />
              Le fichier Excel filtré sera joint en pièce jointe
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmail(false)}>Annuler</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailRecipients.trim()} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="send-email-btn">
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calcul des primes du mois */}
      <Dialog open={showCalcul} onOpenChange={setShowCalcul}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="calcul-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-orange-600" /> Primes à donner — par mois</DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label>Mois concerné de la prime</Label>
              <div className="flex items-center gap-2">
                <select
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={parseInt(calculMonth.split('-')[1], 10)}
                  onChange={e => setCalculMonth(`${calculMonth.split('-')[0]}-${String(parseInt(e.target.value, 10)).padStart(2, '0')}`)}
                  data-testid="calcul-month-select"
                >
                  {['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'].map((label, i) => (
                    <option key={i + 1} value={i + 1}>{label}</option>
                  ))}
                </select>
                <select
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={parseInt(calculMonth.split('-')[0], 10)}
                  onChange={e => setCalculMonth(`${e.target.value}-${calculMonth.split('-')[1]}`)}
                  data-testid="calcul-year-select"
                >
                  {(() => {
                    const cy = new Date().getFullYear();
                    const years = [];
                    for (let y = cy + 1; y >= cy - 3; y--) years.push(y);
                    return years.map(y => <option key={y} value={y}>{y}</option>);
                  })()}
                </select>
              </div>
            </div>
            <Button onClick={() => runCalcul()} disabled={loadingCalcul} className="gap-1 bg-orange-500 hover:bg-orange-600" data-testid="calcul-run">
              {loadingCalcul ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Calculer
            </Button>
            <Button onClick={openBulkCommerciaux} disabled={!calculData} variant="outline" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="send-all-btn">
              <Send className="w-4 h-4" /> Envoyer aux commerciaux
            </Button>
            <Button onClick={openBulkDirection} disabled={!calculData} variant="outline" className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="send-direction-btn">
              <Send className="w-4 h-4" /> Envoyer à la direction
            </Button>
            <Button onClick={exportMois} disabled={!calculData} variant="outline" className="gap-1" data-testid="export-mois-btn">
              <Download className="w-4 h-4" /> Export Excel
            </Button>
            <Button onClick={() => openAddPrimeFor('')} variant="outline" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="calcul-add-line">
              <Plus className="w-4 h-4" /> Ajouter une ligne
            </Button>
            {calculData && (
              <p className="text-xs text-slate-500 ml-auto">Ouvertures M-1 : <b>{calculData.opening_month_label}</b> · Delta 12 mois : <b>{calculData.anniversary_month_label}</b></p>
            )}
          </div>

          {/* Message d'accompagnement */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="calcul-comment-block">
            <Label className="flex items-center gap-1 text-sm"><Mail className="w-4 h-4 text-emerald-600" /> Message commun (appliqué par défaut à tous les commerciaux)</Label>
            <textarea
              className="w-full min-h-[70px] mt-1.5 p-2 border rounded-lg text-sm"
              placeholder="Ex : Bravo à tous pour ce beau mois ! N'hésitez pas à revenir vers moi pour toute question sur votre relevé."
              value={calculComment}
              onChange={e => setCalculComment(e.target.value)}
              data-testid="calcul-comment"
            />
            <p className="text-[11px] text-slate-400 mt-1">Ce texte apparaît dans chaque email (encadré orange). Vous pouvez le personnaliser par commercial dans chaque bloc ci-dessous. Laissez vide pour n'envoyer que le récap standard.</p>
            <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="templates-common">
              <span className="text-[11px] font-medium text-slate-500">Modèles :</span>
              {templates.length === 0 && <span className="text-[11px] text-slate-400">aucun</span>}
              {templates.map(t => (
                <span key={t.id} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 hover:bg-slate-200 rounded-full pl-2 pr-1 py-0.5 cursor-pointer" data-testid={`tpl-common-${t.id}`}>
                  <span onClick={() => setCalculComment(t.content)} title="Appliquer ce modèle">{t.name}</span>
                  <button onClick={() => deleteTemplate(t.id)} className="text-slate-400 hover:text-red-500" title="Supprimer"><X className="w-3 h-3" /></button>
                </span>
              ))}
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-emerald-700" onClick={() => saveTemplate(calculComment)} data-testid="save-template-btn">
                + Enregistrer ce message
              </Button>
            </div>
          </div>

          {loadingCalcul ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : !calculData ? null : (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-blue-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600">{fmtCurrency(calculData.total_nouveaux)}</p><p className="text-xs text-slate-500">Nouveaux contrats</p></CardContent></Card>
                <Card className="border-violet-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-violet-600">{fmtCurrency(calculData.total_renouvellements)}</p><p className="text-xs text-slate-500">Renouvellements</p></CardContent></Card>
                <Card className="border-amber-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-600">{fmtCurrency(calculData.total_delta)}</p><p className="text-xs text-slate-500">Delta 12 mois</p></CardContent></Card>
              </div>

              {calculData.commerciaux.filter(g => g.nouveaux.length || g.renouvellements.length || g.deltas.length || (g.trimestrielles || []).length).length === 0 ? (
                <p className="text-center py-8 text-slate-400">Aucune prime à donner pour {calculData.prime_month_label}.</p>
              ) : calculData.commerciaux.filter(g => g.nouveaux.length || g.renouvellements.length || g.deltas.length || (g.trimestrielles || []).length).map(g => (
                <div key={g.commercial} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`calcul-commercial-${g.commercial}`}>
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b flex-wrap gap-2">
                    <span className="font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> {g.commercial}
                      <span className="text-sm font-bold text-orange-600 ml-2">Total : {fmtCurrency(g.total_commercial)}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {emailsMap[g.commercial] && <span className="text-xs text-slate-400">{emailsMap[g.commercial]}</span>}
                      <Button size="sm" variant="outline" className="gap-1 h-7 border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => openAddPrimeFor(g.commercial)} data-testid={`add-line-${g.commercial}`}>
                        <Plus className="w-3 h-3" /> Ligne
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => previewEmail(g.commercial)} data-testid={`send-email-${g.commercial}`}>
                        <Mail className="w-3 h-3" /> Envoyer Excel
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-xs text-slate-500 border-b bg-white">
                        <th className="text-left px-3 py-1.5">Catégorie</th>
                        <th className="text-left px-3 py-1.5">Nom du compte</th>
                        <th className="text-left px-3 py-1.5">Date ouverture prévi.</th>
                        <th className="text-right px-3 py-1.5">CA (€)</th>
                        <th className="text-center px-3 py-1.5">Taux com.</th>
                        <th className="text-right px-3 py-1.5">Prime (€)</th>
                        <th className="text-center px-3 py-1.5 w-10"></th>
                      </tr></thead>
                      <tbody>
                        {[['nouveau', g.nouveaux], ['renouvellement', g.renouvellements], ['delta', g.deltas]].flatMap(([cat, rows]) =>
                          rows.map(r => (
                            <tr key={r.id + cat} className="border-b border-slate-50">
                              <td className="px-3 py-1.5">
                                <Badge className={`text-[10px] ${cat === 'nouveau' ? 'bg-blue-100 text-blue-700' : cat === 'renouvellement' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {cat === 'nouveau' ? 'Nouveau' : cat === 'renouvellement' ? 'Renouv.' : 'Delta 12M'}
                                </Badge>
                              </td>
                              <td className="px-3 py-1.5">{r.client}</td>
                              <td className="px-3 py-1.5">{fmtDate(r.date_mise_en_place)}</td>
                              <td className="px-3 py-1.5 text-right">
                                <input
                                  key={`ca-${r.id}-${cat}-${cat === 'delta' ? (r.ca_12_mois ?? '') : (r.ca_previsionnel ?? '')}`}
                                  type="number"
                                  defaultValue={cat === 'delta' ? (r.ca_12_mois ?? '') : (r.ca_previsionnel ?? '')}
                                  className="w-28 text-right border border-slate-200 rounded px-1.5 py-0.5 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-300 outline-none"
                                  placeholder="CA €"
                                  onBlur={e => {
                                    const raw = e.target.value;
                                    const v = raw === '' ? null : parseFloat(raw);
                                    const orig = cat === 'delta' ? (r.ca_12_mois ?? null) : (r.ca_previsionnel ?? null);
                                    if (v !== orig) updateRecapField(r, cat === 'delta' ? { ca_12_mois: v } : { ca_previsionnel: v });
                                  }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  data-testid={`recap-ca-${r.id}`}
                                />
                              </td>
                              <td className="px-3 py-1.5 text-center whitespace-nowrap">
                                <input
                                  key={`tx-${r.id}-${cat}-${r.commission_pct ?? ''}`}
                                  type="number"
                                  defaultValue={r.commission_pct ?? ''}
                                  className="w-14 text-center border border-slate-200 rounded px-1 py-0.5 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-300 outline-none"
                                  onBlur={e => {
                                    const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    if (v !== (r.commission_pct ?? 0)) updateRecapField(r, { commission_pct: v });
                                  }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  data-testid={`recap-taux-${r.id}`}
                                /> %
                              </td>
                              <td className="px-3 py-1.5 text-right font-medium">
                                {r.needs_input ? <span className="text-amber-600 text-xs">CA 12M à saisir</span> :
                                  <span className={cat === 'delta' ? (r.prime > 0 ? 'text-emerald-600' : r.prime < 0 ? 'text-red-600' : '') : 'text-emerald-600'}>
                                    {cat === 'delta' && r.prime > 0 ? '+' : ''}{fmtCurrency(r.prime)}{cat === 'renouvellement' ? <span className="text-[10px] text-slate-400">/mois</span> : ''}
                                  </span>}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                <button onClick={() => deleteRecapLine(r.id)} className="text-slate-300 hover:text-red-500 transition-colors" title="Supprimer cette ligne (ne pas primer)" data-testid={`recap-delete-${r.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                        {(g.trimestrielles || []).map(r => (
                          <tr key={r.id} className="border-b border-emerald-100 bg-emerald-50/60" data-testid={`recap-trimestrielle-${g.commercial}-${r.trimestre_label}`}>
                            <td className="px-3 py-1.5">
                              <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Trimestriel</Badge>
                            </td>
                            <td className="px-3 py-1.5 font-medium text-emerald-800">Prime trimestrielle {r.trimestre_label} · {r.count} contrat{r.count > 1 ? 's' : ''} prospect</td>
                            <td className="px-3 py-1.5 text-slate-400">—</td>
                            <td className="px-3 py-1.5 text-right text-slate-400">—</td>
                            <td className="px-3 py-1.5 text-center text-slate-400">—</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{fmtCurrency(r.prime)}</td>
                            <td className="px-2 py-1.5"></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-2 border-t bg-white">
                    <Label className="text-[11px] text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Message pour {g.commercial} (optionnel — sinon le message commun est utilisé)</Label>
                    <textarea
                      className="w-full min-h-[46px] mt-1 p-2 border rounded-lg text-xs"
                      placeholder="Message spécifique à ce commercial…"
                      value={commentsMap[g.commercial] || ''}
                      onChange={e => setCommentsMap(prev => ({ ...prev, [g.commercial]: e.target.value }))}
                      data-testid={`comment-${g.commercial}`}
                    />
                    {templates.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        <span className="text-[10px] text-slate-400">Modèles :</span>
                        {templates.map(t => (
                          <button key={t.id} onClick={() => setCommentsMap(prev => ({ ...prev, [g.commercial]: t.content }))} className="text-[10px] bg-slate-100 hover:bg-slate-200 rounded-full px-2 py-0.5" data-testid={`tpl-${g.commercial}-${t.id}`}>{t.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalcul(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analyse 12 mois */}
      <Dialog open={showAnalyse} onOpenChange={setShowAnalyse}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="analyse-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-amber-600" /> Analyse des régularisations 12 mois</DialogTitle>
            <p className="text-sm text-slate-500">Nouveaux contrats ayant atteint 12 mois de mise en place — delta + (à verser) / − (à reprendre) par commercial.</p>
          </DialogHeader>
          {loadingAnalyse ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : !analyse ? null : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-slate-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-slate-800">{analyse.total_dossiers}</p><p className="text-xs text-slate-500">Dossiers 12 mois+</p></CardContent></Card>
                <Card className="border-emerald-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600">+{fmtCurrency(analyse.total_a_verser)}</p><p className="text-xs text-slate-500">À verser</p></CardContent></Card>
                <Card className="border-red-200"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-600">{fmtCurrency(analyse.total_a_reprendre)}</p><p className="text-xs text-slate-500">À reprendre</p></CardContent></Card>
              </div>
              {analyse.commerciaux.length === 0 ? (
                <p className="text-center py-8 text-slate-400">Aucun dossier n'a encore atteint 12 mois.</p>
              ) : analyse.commerciaux.map(g => (
                <div key={g.commercial} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`analyse-commercial-${g.commercial}`}>
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                    <span className="font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> {g.commercial}</span>
                    <span className="text-sm">
                      {g.nb_a_regulariser > 0 && <Badge variant="destructive" className="mr-2 text-[10px]">{g.nb_a_regulariser} à saisir</Badge>}
                      <span className={`font-bold ${g.delta_total > 0 ? 'text-emerald-600' : g.delta_total < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        Delta : {g.delta_total > 0 ? '+' : ''}{fmtCurrency(g.delta_total)}
                      </span>
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {g.dossiers.map(d => (
                      <div key={d.id} className="flex items-center justify-between px-4 py-2 text-sm">
                        <div>
                          <span className="font-medium">{d.client}</span>
                          <span className="text-xs text-slate-400 ml-2">mis en place {fmtDate(d.date_mise_en_place)} · {d.months_elapsed} mois</span>
                        </div>
                        {d.needs_input ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1"><AlertTriangle className="w-3 h-3" /> CA 12 mois à saisir</Badge>
                        ) : (
                          <Badge className={`${d.ajustement > 0 ? 'bg-emerald-100 text-emerald-700' : d.ajustement < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                            {d.ajustement > 0 ? '+' : ''}{fmtCurrency(d.ajustement)}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalyse(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Primes trimestrielles / annuelles (volume) */}
      <Dialog open={showVolume} onOpenChange={setShowVolume}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="volume-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-600" /> Primes trimestrielles & annuelles — volume de nouveaux contrats signés</DialogTitle>
            <p className="text-sm text-slate-500">Comptage sur l'<b>année civile</b> (1er janv. → 31 déc.) des <b>nouveaux contrats signés</b> (date de signature). Trimestre : dès 7 contrats → 750 € + 200 €/contrat suppl. · Super bonus (palier max) : 15→+1500 €, 20→+3000 €. Annuel : dès 25 → 1500 € + 250 €/contrat suppl. · Super bonus : 40→+3000 €, 60→+6000 €.</p>
          </DialogHeader>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label>Année civile</Label>
              <Input type="number" value={volumeYear} onChange={e => setVolumeYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-32" data-testid="volume-year" />
            </div>
            <Button onClick={() => runVolume()} disabled={loadingVolume} className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="volume-run">
              {loadingVolume ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Calculer
            </Button>
            <Button onClick={exportVolume} disabled={!volumeData} variant="outline" className="gap-1" data-testid="export-volume-btn">
              <Download className="w-4 h-4" /> Export Excel
            </Button>
            {volumeData && (
              <div className="ml-auto flex items-center gap-2 text-xs">
                <Badge className="bg-indigo-100 text-indigo-700">Trimestriel : {fmtCurrency(volumeData.total_trimestriel)}</Badge>
                <Badge className="bg-amber-100 text-amber-700">Annuel : {fmtCurrency(volumeData.total_annuel)}</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">Total : {fmtCurrency(volumeData.total_global)}</Badge>
              </div>
            )}
          </div>

          {loadingVolume ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : !volumeData ? null : volumeData.commerciaux.length === 0 ? (
            <p className="text-center py-8 text-slate-400">Aucun nouveau contrat signé en {volumeData.year}.</p>
          ) : (
            <div className="space-y-5 mt-2">
              {/* Classement annuel */}
              {(() => {
                const ranked = [...volumeData.commerciaux].filter(g => g.annual_count > 0).sort((a, b) => b.annual_count - a.annual_count);
                if (ranked.length === 0) return null;
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <div className="border border-amber-200 rounded-xl bg-amber-50/40 overflow-hidden" data-testid="volume-leaderboard">
                    <div className="px-4 py-2 bg-amber-100/70 border-b border-amber-200 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-800">Classement {volumeData.year} — contrats signés</span>
                    </div>
                    <div className="divide-y divide-amber-100">
                      {ranked.map((g, i) => (
                        <div key={g.commercial} className={`flex items-center justify-between px-4 py-2 ${i === 0 ? 'bg-amber-50' : ''}`} data-testid={`leaderboard-row-${i}`}>
                          <span className="flex items-center gap-2 font-medium text-slate-700">
                            <span className="w-6 text-center">{medals[i] || `${i + 1}.`}</span>
                            {g.commercial}
                          </span>
                          <div className="flex items-center gap-3">
                            <Badge className={`text-xs ${i === 0 ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'}`}>{g.annual_count} contrat{g.annual_count > 1 ? 's' : ''}</Badge>
                            <span className="text-sm font-bold text-indigo-600 w-24 text-right">{fmtCurrency(g.total_commercial)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {volumeData.commerciaux.map(g => (
                <div key={g.commercial} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`volume-commercial-${g.commercial}`}>
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b flex-wrap gap-2">
                    <span className="font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> {g.commercial}
                      <span className="text-sm font-bold text-indigo-600 ml-2">Total {volumeData.year} : {fmtCurrency(g.total_commercial)}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {emailsMap[g.commercial] && <span className="text-xs text-slate-400">{emailsMap[g.commercial]}</span>}
                      <Button size="sm" variant="outline" className="gap-1 h-7" disabled={sendingVolume === g.commercial} onClick={() => sendVolumeEmail(g.commercial)} data-testid={`volume-send-${g.commercial}`}>
                        {sendingVolume === g.commercial ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Envoyer Excel
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                    {g.quarters.map(q => (
                      <div key={q.quarter} className={`rounded-lg border p-3 ${q.near_super_bonus ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-300' : q.near_base_prime ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300' : q.total > 0 ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200'}`} data-testid={`volume-quarter-${g.commercial}-${q.quarter}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-700">{q.label}</span>
                          <Badge variant="secondary" className="text-[10px]">{q.count} contrat{q.count !== 1 ? 's' : ''}</Badge>
                        </div>
                        <p className="text-lg font-bold text-indigo-700 mt-1">{fmtCurrency(q.total)}</p>
                        {q.near_base_prime && (
                          <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-1" data-testid={`near-base-${g.commercial}-${q.quarter}`}>
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                            <span>Plus que {q.base_prime_gap} contrat{q.base_prime_gap > 1 ? 's' : ''} → prime 750 € débloquée !</span>
                          </div>
                        )}
                        {q.near_super_bonus && (
                          <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-orange-700 bg-orange-100 rounded px-1.5 py-1" data-testid={`near-bonus-${g.commercial}-${q.quarter}`}>
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                            <span>Plus que {q.super_bonus_gap} contrat{q.super_bonus_gap > 1 ? 's' : ''} → super bonus {q.super_bonus_target === 20 ? '+3000 €' : '+1500 €'} !</span>
                          </div>
                        )}
                        {(() => {
                          const pr = quarterProgress(q.count);
                          return (
                            <div className="mt-1.5" data-testid={`quarter-progress-${g.commercial}-${q.quarter}`}>
                              <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${pr.maxed ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${pr.pct}%` }} />
                              </div>
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                {pr.maxed ? 'Palier max atteint 🎉' : `${q.count}/${pr.target} vers le palier`}
                              </p>
                            </div>
                          );
                        })()}
                        <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                          <div>Base : {fmtCurrency(q.base_prime)}</div>
                          {q.super_bonus > 0 && <div className="text-amber-600 font-medium">Super bonus : +{fmtCurrency(q.super_bonus)}</div>}
                          <div className="text-slate-400">Versé en {q.pay_month_label}</div>
                          {q.next_threshold != null && q.next_threshold > 0 && (
                            <div className="text-emerald-600">+{q.next_threshold} contrat{q.next_threshold > 1 ? 's' : ''} → palier suivant</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 bg-amber-50/60 border-t space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                        Prime annuelle {volumeData.year}
                        <Badge variant="secondary" className="text-[10px]">{g.annual_count} contrat{g.annual_count !== 1 ? 's' : ''}</Badge>
                      </span>
                      <div className="flex items-center gap-3 text-xs text-slate-600">
                        <span>Base : <b>{fmtCurrency(g.annual_base_prime)}</b></span>
                        {g.annual_super_bonus > 0 && <span className="text-amber-600">Super bonus : <b>+{fmtCurrency(g.annual_super_bonus)}</b></span>}
                        <span className="text-slate-400">Versée en {g.annual_pay_month_label}</span>
                        <span className="text-base font-bold text-amber-700">{fmtCurrency(g.annual_total)}</span>
                      </div>
                    </div>
                    {(() => {
                      const ap = annualProgress(g.annual_count);
                      return (
                        <div data-testid={`annual-progress-${g.commercial}`}>
                          <div className="h-2 w-full bg-amber-200/60 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${ap.maxed ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${ap.pct}%` }} />
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-amber-700/80 mt-0.5">
                            <span>{ap.maxed ? 'Objectif max annuel atteint 🎉' : `${g.annual_count}/${ap.target} contrats vers le palier`}</span>
                            <span className="text-slate-400">Paliers : 25 · 40 · 60</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVolume(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aperçu du mail */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-slate-600" /> Aperçu du mail — {previewCommercial}</DialogTitle>
          </DialogHeader>
          {loadingPreview ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : previewData ? (
            <div className="space-y-3">
              {/* Choix des destinataires */}
              <div className="border rounded-lg p-3 bg-slate-50/60">
                <Label className="text-sm flex items-center gap-1"><Send className="w-4 h-4 text-emerald-600" /> Destinataires</Label>
                {savedRecipients.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[11px] text-slate-400 mb-1">Adresses enregistrées (cochez celles à qui envoyer) :</p>
                    <div className="flex flex-wrap gap-2" data-testid="saved-recipients">
                      {savedRecipients.map(email => (
                        <button
                          key={email}
                          type="button"
                          onClick={() => toggleRecipient(email)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selectedRecipients.includes(email) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'}`}
                          data-testid={`recipient-chip-${email}`}
                        >
                          {selectedRecipients.includes(email) ? '✓ ' : ''}{email}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="text"
                    placeholder="Ajouter une autre adresse…"
                    value={customRecipient}
                    onChange={e => setCustomRecipient(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomRecipient(); } }}
                    className="h-9 flex-1"
                    data-testid="custom-recipient-input"
                  />
                  <Button size="sm" variant="outline" className="h-9" onClick={addCustomRecipient} data-testid="add-recipient-btn">Ajouter</Button>
                </div>
                {selectedRecipients.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="selected-recipients">
                    {selectedRecipients.map(email => (
                      <span key={email} className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
                        {email}
                        <button type="button" onClick={() => toggleRecipient(email)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-amber-600 mt-2">Aucun destinataire sélectionné.</p>
                )}
              </div>

              <div className="text-sm space-y-2 bg-slate-50 rounded-lg p-3 border">
                <div>
                  <Label className="text-xs text-slate-500">Objet</Label>
                  <Input value={previewSubject} onChange={e => setPreviewSubject(e.target.value)} className="h-9 mt-1" data-testid="preview-subject" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Message personnalisé (optionnel — précisions pour ce commercial)</Label>
                  <textarea
                    className="w-full min-h-[70px] p-2 border rounded-md text-sm mt-1"
                    placeholder="Ex. Attention, prime Lure à confirmer. Pensez à vérifier le CA de décembre…"
                    value={previewMessage}
                    onChange={e => setPreviewMessage(e.target.value)}
                    data-testid="preview-message"
                  />
                  <div className="flex justify-end mt-1">
                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={refreshPreview} data-testid="preview-refresh-btn">
                      <Eye className="w-3 h-3" /> Mettre à jour l'aperçu
                    </Button>
                  </div>
                </div>
                <div><span className="text-slate-400">Pièces jointes :</span> {(previewData.attachments || [previewData.attachment_name]).map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 mr-2 text-slate-700"><span className="text-slate-300">📎</span>{a}</span>
                ))}</div>
              </div>
              <div className="border rounded-lg p-4 bg-white" data-testid="preview-body" dangerouslySetInnerHTML={{ __html: previewData.html }} />
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Fermer</Button>
            {previewData && (
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1" disabled={sendingPreview || selectedRecipients.length === 0} onClick={sendFromPreview} data-testid="preview-send-btn">
                {sendingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Envoyer ({selectedRecipients.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aperçu + édition avant envoi groupé (commerciaux / direction) */}
      <Dialog open={bulk.open} onOpenChange={(v) => setBulk(b => ({ ...b, open: v }))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-slate-600" />
              {bulk.mode === 'direction' ? 'Aperçu du mail — Direction (récap consolidé)' : 'Aperçu du mail — Commerciaux'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {bulk.mode === 'commerciaux' ? (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2.5" data-testid="bulk-info">
                Chaque commercial reçoit <b>son propre relevé</b> (avec son fichier Excel). L'objet et le message ci-dessous s'appliquent à <b>tous</b> les envois. Aperçu basé sur : <b>{bulk.sampleName}</b>.
              </div>
            ) : (
              <div className="border rounded-lg p-3 bg-slate-50/60">
                <Label className="text-sm flex items-center gap-1"><Send className="w-4 h-4 text-blue-600" /> Destinataires Direction</Label>
                <p className="text-[11px] text-slate-400 mt-1 mb-1">Plusieurs adresses séparées par une virgule (pré-rempli depuis Config).</p>
                <Input type="text" placeholder="direction@wasabi.fr, daf@wasabi.fr" value={bulk.recipients} onChange={e => setBulk(b => ({ ...b, recipients: e.target.value }))} className="h-9" data-testid="bulk-direction-recipients" />
              </div>
            )}

            {bulk.loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
            ) : (
              <>
                <div className="text-sm space-y-2 bg-slate-50 rounded-lg p-3 border">
                  <div>
                    <Label className="text-xs text-slate-500">Objet</Label>
                    <Input value={bulk.subject} onChange={e => setBulk(b => ({ ...b, subject: e.target.value }))} className="h-9 mt-1" data-testid="bulk-subject" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Message (texte ajouté en haut de l'email)</Label>
                    <textarea
                      className="w-full min-h-[80px] p-2 border rounded-md text-sm mt-1"
                      placeholder="Ex. Bonjour, voici le récapitulatif des primes du mois…"
                      value={bulk.message}
                      onChange={e => setBulk(b => ({ ...b, message: e.target.value }))}
                      data-testid="bulk-message"
                    />
                    <div className="flex justify-end mt-1">
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={refreshBulkPreview} data-testid="bulk-refresh-btn">
                        <Eye className="w-3 h-3" /> Mettre à jour l'aperçu
                      </Button>
                    </div>
                  </div>
                  {bulk.attachment && (
                    <div><span className="text-slate-400">Pièce jointe :</span> <span className="inline-flex items-center gap-1 text-slate-700"><span className="text-slate-300">📎</span>{bulk.attachment}</span></div>
                  )}
                </div>
                <div className="border rounded-lg p-4 bg-white" data-testid="bulk-preview-body" dangerouslySetInnerHTML={{ __html: bulk.html }} />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(b => ({ ...b, open: false }))}>Annuler</Button>
            <Button className={bulk.mode === 'direction' ? 'bg-blue-600 hover:bg-blue-700 gap-1' : 'bg-emerald-600 hover:bg-emerald-700 gap-1'} disabled={bulk.sending || bulk.loading} onClick={sendBulk} data-testid="bulk-send-btn">
              {bulk.sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {bulk.mode === 'direction' ? 'Envoyer à la direction' : 'Envoyer à tous les commerciaux'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conditions PDF éditables */}
      <Dialog open={showConditions} onOpenChange={setShowConditions}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="conditions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-orange-500" /> Conditions variables des commerciaux (PDF joint aux mails)</DialogTitle>
            <p className="text-sm text-slate-500">Modifiez le texte ci-dessous : il génère le PDF « Conditions variables des commerciaux.pdf » attaché à chaque email de primes.</p>
          </DialogHeader>
          {loadingConditions ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-3">
              <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2 border">
                <b>Syntaxe :</b> <code>## Titre de section</code> · <code>- puce</code> · <code>&gt; ligne d'exemple (indentée)</code> · <code>**gras**</code> · ligne vide = espacement.
              </div>
              <textarea
                className="w-full min-h-[380px] p-3 border rounded-lg text-sm font-mono leading-relaxed"
                value={conditionsText}
                onChange={e => setConditionsText(e.target.value)}
                data-testid="conditions-textarea"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={previewConditionsPdf} className="gap-1" data-testid="conditions-preview-btn"><Eye className="w-4 h-4" /> Aperçu PDF</Button>
            <Button variant="outline" onClick={() => setShowConditions(false)}>Fermer</Button>
            <Button onClick={saveConditions} disabled={savingConditions} className="bg-orange-500 hover:bg-orange-600 gap-1" data-testid="conditions-save-btn">
              {savingConditions ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historique des envois */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-slate-600" /> Historique des envois de primes</DialogTitle>
            <p className="text-sm text-slate-500">Récapitulatifs déjà envoyés par email (mois & volume), du plus récent au plus ancien.</p>
          </DialogHeader>
          {loadingHistory ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : historyData.length === 0 ? (
            <p className="text-center py-8 text-slate-400">Aucun envoi enregistré pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b">
                  <th className="px-3 py-2">Date d'envoi</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Période</th>
                  <th className="px-3 py-2">Commercial</th>
                  <th className="px-3 py-2">Destinataires</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr></thead>
                <tbody>
                  {historyData.map((h, i) => (
                    <tr key={h.id || i} className="border-b hover:bg-slate-50" data-testid={`history-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{h.sent_at ? new Date(h.sent_at).toLocaleString('fr-FR') : '—'}</td>
                      <td className="px-3 py-2">
                        {h.type === 'volume'
                          ? <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Trim./Annuel</Badge>
                          : <Badge className="bg-orange-100 text-orange-700 text-[10px]">Mensuel{h.batch ? ' (groupé)' : ''}</Badge>}
                      </td>
                      <td className="px-3 py-2">{h.period_label || h.period}</td>
                      <td className="px-3 py-2 font-medium">{h.commercial}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{Array.isArray(h.recipients) ? h.recipients.join(', ') : h.recipients}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtCurrency(h.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
